/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';

import {ApiConfig} from '../../../config/api-config';
import {LocalUserProfile} from '../../../../components/session/local-user-profile';
import template from './adn-invoicing-page.component.html';
import {
  AdnInvoicingApiService,
  AdnInvoicingEnqueueResult,
  AdnInvoicingJob,
  AdnInvoicingPeriod
} from './adn-invoicing-api.service';
import {
  invoicingPeriodDateRangeLabel,
  invoicingPeriodTypeLabel,
  previousIsoWeekValue,
  previousMonth,
  recentIsoWeekOptions,
  validCustomPeriod
} from './adn-invoicing-period.util';
import {visiblePageNumbers} from '../../../common/directives/pagination/visible-page-numbers';

const POLL_INTERVAL_MS = 1000;
const HISTORY_PAGE_SIZE = 25;

@Component({
  standalone: false,
  selector: 'adn-invoicing-page',
  template,
  providers: [AdnInvoicingApiService]
})
export class AdnInvoicingPageComponent implements OnInit, OnDestroy {
  readonly allowed: boolean;
  readonly showMarketplaceNetworkId = this.apiConfig.getEnv() !== 'production';
  readonly weekOptions = recentIsoWeekOptions();
  mode: 'month' | 'week' | 'custom' = 'month';
  marketplaceNetworkId = 'b7462';
  month = '';
  week = '';
  customStartDate = '';
  customEndDate = '';
  readonly historyPageSize = HISTORY_PAGE_SIZE;

  private readonly runningState = signal(false);
  private readonly downloadingJobIdState = signal<string | null>(null);
  private readonly uploadingJobIdState = signal<string | null>(null);
  private readonly viewingJobIdState = signal<string | null>(null);
  private readonly historyLoadingState = signal(false);
  private readonly historyPageState = signal(1);
  private readonly historyTotalCountState = signal(0);
  private readonly errorState = signal('');
  private readonly historyErrorState = signal('');
  private readonly uploadErrorState = signal('');
  private readonly reusedState = signal(false);
  private readonly jobState = signal<AdnInvoicingJob | null>(null);
  private readonly previousRunsState = signal<AdnInvoicingJob[]>([]);

  private pollTimer: number | null = null;
  private destroyed = false;
  private historyRequest = 0;
  private requestedPeriod: AdnInvoicingPeriod | null = null;

  constructor(
    private readonly api: AdnInvoicingApiService,
    @Inject(LocalUserProfile) localUserProfile: LocalUserProfile,
    private readonly apiConfig: ApiConfig
  ) {
    this.allowed = localUserProfile.isSpecialUser();
  }

  get running(): boolean { return this.runningState(); }
  set running(value: boolean) { this.runningState.set(value); }
  get downloadingJobId(): string | null { return this.downloadingJobIdState(); }
  set downloadingJobId(value: string | null) { this.downloadingJobIdState.set(value); }
  get uploadingJobId(): string | null { return this.uploadingJobIdState(); }
  set uploadingJobId(value: string | null) { this.uploadingJobIdState.set(value); }
  get viewingJobId(): string | null { return this.viewingJobIdState(); }
  set viewingJobId(value: string | null) { this.viewingJobIdState.set(value); }
  get historyLoading(): boolean { return this.historyLoadingState(); }
  set historyLoading(value: boolean) { this.historyLoadingState.set(value); }
  get historyPage(): number { return this.historyPageState(); }
  set historyPage(value: number) { this.historyPageState.set(value); }
  get historyTotalCount(): number { return this.historyTotalCountState(); }
  set historyTotalCount(value: number) { this.historyTotalCountState.set(value); }
  get error(): string { return this.errorState(); }
  set error(value: string) { this.errorState.set(value); }
  get historyError(): string { return this.historyErrorState(); }
  set historyError(value: string) { this.historyErrorState.set(value); }
  get uploadError(): string { return this.uploadErrorState(); }
  set uploadError(value: string) { this.uploadErrorState.set(value); }
  get reused(): boolean { return this.reusedState(); }
  set reused(value: boolean) { this.reusedState.set(value); }
  get job(): AdnInvoicingJob | null { return this.jobState(); }
  set job(value: AdnInvoicingJob | null) { this.jobState.set(value); }
  get previousRuns(): AdnInvoicingJob[] { return this.previousRunsState(); }
  set previousRuns(value: AdnInvoicingJob[]) { this.previousRunsState.set(value); }

  ngOnInit(): void {
    this.month = previousMonth();
    this.week = previousIsoWeekValue();
    const [year, month] = this.month.split('-').map(Number);
    this.customStartDate = `${this.month}-01`;
    this.customEndDate = `${this.month}-${String(new Date(Date.UTC(year, month, 0)).getUTCDate()).padStart(2, '0')}`;
    if (this.allowed) void this.loadPreviousRuns();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearPoll();
  }

  async start(): Promise<void> {
    if (!this.allowed || this.running || !this.valid()) return;
    this.clearPoll();
    this.error = '';
    this.reused = false;
    this.job = null;
    this.running = true;
    try {
      this.requestedPeriod = this.period();
      this.applyEnqueueResult(await this.api.enqueue(this.requestedPeriod));
    } catch (error) {
      this.handleEnqueueError(error);
    }
  }

  async recreate(): Promise<void> {
    if (!this.allowed || this.running || !this.reused || !this.requestedPeriod) return;
    this.clearPoll();
    this.error = '';
    this.reused = false;
    this.running = true;
    try {
      this.applyEnqueueResult(await this.api.enqueue(this.requestedPeriod, true));
    } catch (error) {
      this.handleEnqueueError(error);
    }
  }

  async refresh(): Promise<void> {
    if (!this.allowed || !this.job || isTerminal(this.job.status)) return;
    this.clearPoll();
    try {
      const job = await this.api.get(this.job.id);
      this.applyJob(job);
      if (isTerminal(job.status)) await this.loadPreviousRuns();
    } catch (error) {
      this.running = false;
      this.error = errorMessage(error);
    }
  }

  async loadPreviousRuns(page = this.historyPage): Promise<void> {
    if (!this.allowed) return;
    const request = ++this.historyRequest;
    this.historyPage = page;
    this.historyLoading = true;
    this.historyError = '';
    try {
      const previousRuns = await this.api.query(page, this.historyPageSize);
      if (request === this.historyRequest) {
        this.previousRuns = previousRuns.results || [];
        this.historyTotalCount = previousRuns.totalCount || 0;
      }
    } catch (error) {
      if (request === this.historyRequest) this.historyError = errorMessage(error);
    } finally {
      if (request === this.historyRequest) this.historyLoading = false;
    }
  }

  async viewRun(run: AdnInvoicingJob): Promise<void> {
    if (!this.allowed || this.viewingJobId !== null) return;
    this.clearPoll();
    this.error = '';
    this.reused = false;
    if (isTerminal(run.status)) {
      this.applyJob(run);
      return;
    }
    this.viewingJobId = run.id;
    try {
      this.applyJob(await this.api.get(run.id));
    } catch (error) {
      this.error = errorMessage(error);
    } finally {
      this.viewingJobId = null;
    }
  }

  async downloadResult(event: Event, job: AdnInvoicingJob | null = this.job): Promise<void> {
    event.preventDefault();
    if (!job || !this.isDownloadReady(job) || this.downloadingJobId !== null) return;
    this.downloadingJobId = job.id;
    this.error = '';
    try {
      const blob = await this.api.download(job);
      download(blob, job.archiveName || `adn_invoicing_${job.periodValue}.zip`);
    } catch (error) {
      this.error = errorMessage(error);
    } finally {
      this.downloadingJobId = null;
    }
  }

  async uploadResult(run: AdnInvoicingJob): Promise<void> {
    if (!this.allowed || !this.canUpload(run) || this.uploadingJobId !== null) return;
    this.uploadingJobId = run.id;
    this.uploadError = '';
    try {
      this.replaceJob(await this.api.upload(run.id));
    } catch (error) {
      const current = (error as any)?.data;
      if (isJob(current)) this.replaceJob(current);
      this.uploadError = uploadErrorMessage(error);
    } finally {
      this.uploadingJobId = null;
    }
  }

  changeHistoryPage(page: number, event: Event): void {
    event.preventDefault();
    if (this.historyLoading || page < 1 || page > this.historyTotalPages || page === this.historyPage) return;
    void this.loadPreviousRuns(page);
  }

  valid(): boolean {
    const validPeriod = this.mode === 'month'
      ? Boolean(this.month)
      : this.mode === 'week'
        ? Boolean(this.week)
        : validCustomPeriod(this.customStartDate, this.customEndDate);
    return Boolean(this.marketplaceNetworkId.trim() && validPeriod);
  }

  get downloadReady(): boolean {
    return this.isDownloadReady(this.job);
  }

  isDownloadReady(job: AdnInvoicingJob | null): boolean {
    return job?.status === 'COMPLETED'
      && (job.resultAvailable || Boolean(job.downloadUrl));
  }

  canUpload(job: AdnInvoicingJob): boolean {
    return this.isDownloadReady(job)
      && job.uploadStatus !== 'UPLOADED'
      && job.uploadStatus !== 'PENDING'
      && job.uploadStatus !== 'UPLOADING';
  }

  uploadStatusLabel(job: AdnInvoicingJob): string {
    switch (job.uploadStatus) {
      case 'PENDING': return 'Pending';
      case 'UPLOADING': return 'Uploading';
      case 'UPLOADED': return 'Uploaded';
      case 'FAILED': return 'Failed';
      default: return 'Not uploaded';
    }
  }

  get historyTotalPages(): number {
    return Math.max(1, Math.ceil(this.historyTotalCount / this.historyPageSize));
  }

  get historyPageNumbers(): number[] {
    return visiblePageNumbers(this.historyPage, this.historyTotalPages);
  }

  get historyPageRangeStart(): number {
    return this.historyTotalCount ? this.historyPageSize * (this.historyPage - 1) + 1 : 0;
  }

  get historyPageRangeEnd(): number {
    return Math.min(this.historyPageSize * this.historyPage, this.historyTotalCount);
  }

  formatDuration(durationMillis?: number): string {
    if (durationMillis === undefined) return '—';
    if (durationMillis < 1000) return `${durationMillis} ms`;
    const seconds = Math.round(durationMillis / 1000);
    if (seconds < 60) return `${seconds} s`;
    return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
  }

  periodDateRange(run: AdnInvoicingJob): string {
    return invoicingPeriodDateRangeLabel(run.periodType, run.periodValue);
  }

  periodTypeLabel(run: AdnInvoicingJob): string {
    return invoicingPeriodTypeLabel(run.periodType);
  }

  private applyJob(job: AdnInvoicingJob): void {
    this.job = job;
    this.running = !isTerminal(job.status);
    if (this.running) this.schedulePoll();
  }

  private period(): AdnInvoicingPeriod {
    const marketplaceNetworkId = this.marketplaceNetworkId.trim();
    if (this.mode === 'month') return {marketplaceNetworkId, month: this.month};
    if (this.mode === 'week') return {marketplaceNetworkId, week: this.week};
    return {marketplaceNetworkId, startDate: this.customStartDate, endDate: this.customEndDate};
  }

  private applyEnqueueResult(result: AdnInvoicingEnqueueResult): void {
    this.reused = result.reused;
    this.applyJob(result.job);
    void this.loadPreviousRuns();
  }

  private replaceJob(job: AdnInvoicingJob): void {
    this.previousRuns = this.previousRuns.map((run) => run.id === job.id ? job : run);
    if (this.job?.id === job.id) this.job = job;
  }

  private handleEnqueueError(error: unknown): void {
    const activeJob = (error as any)?.status === 409 ? (error as any)?.data : null;
    if (isJob(activeJob)) {
      this.error = 'Another invoice generation is already active. Showing its progress.';
      this.applyJob(activeJob);
    } else {
      this.running = false;
      this.error = errorMessage(error);
    }
  }

  private schedulePoll(): void {
    this.clearPoll();
    if (this.destroyed) return;
    this.pollTimer = window.setTimeout(() => void this.refresh(), POLL_INTERVAL_MS);
  }

  private clearPoll(): void {
    if (this.pollTimer === null) return;
    window.clearTimeout(this.pollTimer);
    this.pollTimer = null;
  }
}

function isTerminal(status: AdnInvoicingJob['status']): boolean {
  return status === 'COMPLETED' || status === 'FAILED';
}

function isJob(value: any): value is AdnInvoicingJob {
  return Boolean(value?.id && value?.status);
}

function errorMessage(error: any): string {
  const data = error?.data;
  if (typeof data === 'string') return data;
  return data?.message || data?.description || error?.message || 'Invoice generation failed.';
}

function uploadErrorMessage(error: any): string {
  const data = error?.data;
  if (typeof data === 'string') return data;
  if (data?.message || data?.description) return data.message || data.description;
  if (error?.status === 409) return 'This invoice is not ready to upload or its upload is already in progress.';
  if (error?.status === 502) return 'The invoice upload failed. You can retry it.';
  if (error?.status === 503) return 'Invoice uploading is unavailable in this environment.';
  return error?.message || 'Invoice upload failed.';
}

function download(blob: Blob, fileName: string): void {
  const zip = blob.type === 'application/zip' ? blob : new Blob([blob], {type: 'application/zip'});
  const url = URL.createObjectURL(zip);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
